/**
 * Snapshots: what a client is told about the entities near it, and how a client's copy of the
 * World (a replica, drawn by the renderer) takes it in. The server builds `NetEntity`s from its
 * World, sends each client only what changed since that client's last snapshot (`diffEntities`),
 * and the client patches its copy (`patchEntities`), plays it back a little behind the latest
 * (`blendEntities`), and writes the result onto its replica (`applyEntities`).
 *
 * Plain functions over plain data: no sockets, no clocks. The replica path is the only code that
 * writes entities from outside the World's own rules, and it writes only what the server decided.
 */
import type { Appearance } from '../data/appearance';
import type { SkillKind } from '../data/skills';
import type { WeatherState } from '../data/weather';
import { getMonster, MONSTERS } from '../data/monsters';
import type { Entity, EntityKind, World } from '../world';
import { applyKidnap, netKidnap, sameKidnap, type NetKidnap } from './kidnap';

/** The part of an entity a client needs to draw it and show its wind-ups. */
export interface NetEntity {
  id: number;
  kind: EntityKind;
  x: number;
  y: number;
  z: number;
  facing: 1 | -1;
  heading: number;
  dirX: number;
  dirY: number;
  moving: boolean;
  sprinting: boolean;
  dead: boolean;
  blocking: boolean;
  sheathed: boolean;
  hp: number;
  maxHp: number;
  qi: number;
  maxQi: number;
  stunT: number;
  swingT: number;
  hitT: number;
  /** A move being wound up: the combat triangle needs every wind-up shown (`at`: a ground move's landing spot). */
  cast: { skillId: string; kind: SkillKind; t: number; windup: number; at?: { x: number; y: number } } | null;
  mount: string | null;
  emote: string | null;
  /** A townsperson's act (data/npcActs.ts) and the World time it runs to (systems/npcActs.ts `setAct`). */
  act: string | null;
  actUntil: number;
  targetId: number | null;
  /** Offline work and kidnapping (net/kidnap.ts): null for everyone not in it. */
  kidnap?: NetKidnap | null;
  /** A player character's appearance (the creator's choices); left out for everyone else. */
  look?: Appearance;
  /**
   * Name, figure (`Entity.sprite`) and worn looks (`Entity.outfit`, comma-joined): every player, and every
   * other entity that has them, so a replica that did not spawn it (a bout's foe, a room's people, a
   * respawned monster) can draw it and target it (`syncStandIns`). Sent whole when it comes into view.
   */
  name?: string;
  sprite?: string;
  outfit?: string;
  /** Everyone but players (left out when empty or default): the title over the head, a monster's kind (data/monsters.ts), size. */
  title?: string;
  mon?: string;
  scale?: number;
  radius?: number;
}

/** What a player's entity carries besides the shared fields: its look, name, figure and outfit (others: nothing). */
function withPlayer(world: World | null, e: Entity, n: NetEntity): NetEntity {
  const look = world?.players.get(e.id)?.progress.appearance;
  if (!look) return e.kind === 'player' ? n : withLooks(e, n);
  n.look = look;
  n.name = e.name;
  n.sprite = e.sprite;
  n.outfit = e.outfit.join(',');
  return n;
}

/** Entity defaults a stand-in starts from (World `add`): left off the wire. */
const DEFAULT_RADIUS = 0.28;

/** What a replica needs to draw and target an entity it did not spawn (`NetEntity` name … radius). */
function withLooks(e: Entity, n: NetEntity): NetEntity {
  if (e.name) n.name = e.name;
  if (e.title) n.title = e.title;
  if (e.sprite) n.sprite = e.sprite;
  if (e.outfit.length) n.outfit = e.outfit.join(',');
  if (e.brain) n.mon = e.brain.defId;
  if (e.scale !== 1) n.scale = q(e.scale, 100);
  if (e.radius !== DEFAULT_RADIUS) n.radius = q(e.radius, 100);
  return n;
}

/** An entity's id and the fields that changed (all of them for an entity new to the client). */
export type NetEntityPatch = Partial<NetEntity> & { id: number };

/**
 * A move alone, the commonest patch, packed small for the wire: `[id, dx, dy]`, the step from the
 * position last sent in whole centimetres (positions are sent to the centimetre, so it is exact).
 */
export type NetMove = [id: number, dx: number, dy: number];

/** What a snapshot carries per entity: a patch, or a packed move. */
export type NetPatch = NetEntityPatch | NetMove;

/** A patch that holds only a new position goes as a `NetMove` (a third of the bytes). */
function pack(p: NetEntityPatch, was: NetEntity, n: NetEntity): NetPatch {
  for (const k in p) if (k !== 'id' && k !== 'x' && k !== 'y') return p;
  return [p.id, Math.round((n.x - was.x) * 100), Math.round((n.y - was.y) * 100)];
}

/** A position moved by a `NetMove` step, back on the centimetre grid. */
const step = (v: number, d: number): number => Math.round(v * 100 + d) / 100;

/** How far around a viewer entities are sent (tiles, square), a little past monsters' wake range. */
export const INTEREST_RADIUS = 40;

/** A jump this long between two snapshots is a teleport (respawn, blink, warp): not blended. */
const SNAP_DISTANCE = 3;

/** Rounds to a fixed step so tiny float drift does not count as a change on the wire. */
const q = (v: number, step = 1000): number => Math.round(v * step) / step + 0; // + 0: no -0 (JSON drops it)

/** The network view of one entity. */
export function netEntity(e: Entity): NetEntity {
  return {
    id: e.id,
    kind: e.kind,
    // To the centimetre: finer is never drawn, and every digit is paid at 20 Hz per mover.
    x: q(e.x, 100),
    y: q(e.y, 100),
    z: q(e.z, 100),
    facing: e.facing,
    heading: e.heading,
    dirX: q(e.dirX, 100),
    dirY: q(e.dirY, 100),
    moving: e.moving,
    sprinting: e.sprinting,
    dead: e.dead,
    blocking: e.blocking,
    sheathed: e.sheathed,
    hp: Math.round(e.hp),
    maxHp: Math.round(e.maxHp),
    qi: Math.round(e.qi),
    maxQi: Math.round(e.maxQi),
    stunT: q(e.stunT, 100),
    swingT: q(e.swingT, 100),
    hitT: q(e.hitT, 100),
    cast: e.cast
      ? {
          skillId: e.cast.skillId,
          kind: e.cast.kind,
          t: q(e.cast.t, 100),
          windup: e.cast.windup,
          ...(e.cast.at ? { at: { x: q(e.cast.at.x, 100), y: q(e.cast.at.y, 100) } } : {}),
        }
      : null,
    mount: e.mount,
    emote: e.emote,
    act: e.act ?? null,
    actUntil: q(e.actUntil ?? 0, 10),
    targetId: e.targetId,
    kidnap: netKidnap(e),
  };
}

/**
 * Interest management, first cut: every entity within `radius` tiles (square) of `center`, and
 * `center` itself. Sorted by id so the same world gives the same list.
 */
export function entitiesNear(world: World, center: Entity, radius = INTEREST_RADIUS): NetEntity[] {
  const out: NetEntity[] = [];
  for (const e of world.entities.values())
    if (e === center || (Math.abs(e.x - center.x) <= radius && Math.abs(e.y - center.y) <= radius)) {
      // The character's look rides with its entity (sent whole once; the same object is no change).
      out.push(withPlayer(world, e, netEntity(e)));
    }
  return out.sort((a, b) => a.id - b.id);
}

/**
 * Interest areas (docs/archive/OPEN_WORLD.md §4.3): the map in 32 m cells. A client is sent, at the full
 * snapshot rate, everything in the 7 × 7 cells round it (about 112 m, past the monsters' 36 m wake
 * radius); and players, bosses and riders out to 256 m, positions only, at `farHz`.
 */
export const INTEREST = {
  /** Cell size (metres, = tiles). */
  cell: 32,
  /** Cells round the viewer's own sent in full: 3 → 7 × 7. */
  reach: 3,
  /** How far the far tier reaches (metres, round). */
  far: 256,
  /** Far-tier updates per second. */
  farHz: 5,
} as const;

/** The fields a far-tier entity is updated with. */
const FAR_FIELDS = ['x', 'y', 'z', 'heading', 'moving'] as const;

/** Seen from afar: players (and the fake fellow players), bosses, and anyone riding. */
function farSeen(e: Entity): boolean {
  if (e.kind === 'player' || e.kind === 'rival' || e.mount) return true;
  if (e.kind !== 'monster' || !e.brain) return false;
  const def = getMonster(e.brain.defId);
  return def.respawn === null || !!def.aura;
}

const cellKey = (cx: number, cy: number): number => (cx + 32768) * 65536 + (cy + 32768);

/**
 * A World's entities by 32 m cell, and its far-tier entities: built once per snapshot and shared
 * by every client's view, each entity's `NetEntity` made at most once.
 */
export class InterestGrid {
  private readonly cells = new Map<number, Entity[]>();
  private readonly far: Entity[] = [];
  private nets = new Map<number, NetEntity>();
  /** The last build's views: an entity unchanged since keeps the same object (a cheap test). */
  private last = new Map<number, NetEntity>();
  /** Per entity, the patch from one view to the next, made once for every client that needs it. */
  private readonly steps = new Map<number, { from: NetEntity; to: NetEntity; patch: NetPatch | null }>();
  private world: World | null = null;

  build(world: World): this {
    this.world = world;
    this.cells.clear();
    this.far.length = 0;
    [this.last, this.nets] = [this.nets, this.last];
    this.nets.clear();
    for (const id of this.steps.keys()) if (!world.entities.has(id)) this.steps.delete(id);
    const S = INTEREST.cell;
    for (const e of world.entities.values()) {
      const k = cellKey(Math.floor(e.x / S), Math.floor(e.y / S));
      let list = this.cells.get(k);
      if (!list) this.cells.set(k, (list = []));
      list.push(e);
      if (farSeen(e)) this.far.push(e);
    }
    return this;
  }

  /** The network view of an entity (the player's look riding with it), made once per build. */
  net(e: Entity): NetEntity {
    let n = this.nets.get(e.id);
    if (!n) {
      // Every player's look, name and figure (docs/ONLINE.md §2: a server World holds many).
      n = withPlayer(this.world, e, netEntity(e));
      const was = this.last.get(e.id);
      if (was && !diffOne(was, n)) n = was;
      this.nets.set(e.id, n);
    }
    return n;
  }

  /** What takes a client from `from` to `to` (null: nothing), packed; shared by every client. */
  patch(from: NetEntity, to: NetEntity): NetPatch | null {
    const c = this.steps.get(to.id);
    if (c && c.from === from && c.to === to) return c.patch;
    const d = diffOne(from, to);
    const patch = d && pack(d, from, to);
    this.steps.set(to.id, { from, to, patch });
    return patch;
  }

  /**
   * The entities in the 7 × 7 cells round `center` (and `center`), and the far tier's beyond them,
   * in a fixed order (by cell, then as the World holds them).
   */
  view(center: Entity): { near: Entity[]; far: Entity[] } {
    const S = INTEREST.cell;
    const R = INTEREST.reach;
    const cx = Math.floor(center.x / S);
    const cy = Math.floor(center.y / S);
    const near: Entity[] = [];
    for (let y = cy - R; y <= cy + R; y++)
      for (let x = cx - R; x <= cx + R; x++) {
        const list = this.cells.get(cellKey(x, y));
        if (list) for (const e of list) near.push(e);
      }
    // The viewer itself, when it is not in the World's entities (it always is when it is).
    if (this.world?.entities.get(center.id) !== center) near.push(center);
    const far: Entity[] = [];
    const F = INTEREST.far;
    const x0 = (cx - R) * S;
    const x1 = (cx + R + 1) * S;
    const y0 = (cy - R) * S;
    const y1 = (cy + R + 1) * S;
    for (const e of this.far) {
      if (e.x >= x0 && e.x < x1 && e.y >= y0 && e.y < y1) continue;
      const dx = e.x - center.x;
      const dy = e.y - center.y;
      if (dx * dx + dy * dy <= F * F) far.push(e);
    }
    return { near, far };
  }
}

/**
 * One client's snapshot from its interest area: entities in its 7 × 7 cells diffed in full
 * against what it was last sent (`sent`); far-tier entities sent whole when they first come into
 * view, then only their position, and only when `farDue`; and the ids that left both. Returns the
 * patches, the ids gone, and what the client now holds (to pass as `sent` next time).
 */
export function interestSnapshot(
  grid: InterestGrid,
  center: Entity,
  sent: ReadonlyMap<number, NetEntity>,
  farDue: boolean,
): { ents: NetPatch[]; gone: number[]; sent: Map<number, NetEntity> } {
  const { near, far } = grid.view(center);
  const next = new Map<number, NetEntity>();
  const ents: NetPatch[] = [];
  for (const e of near) {
    const n = grid.net(e);
    next.set(n.id, n);
    const p = sent.get(n.id);
    if (!p) ents.push({ ...n });
    else if (p !== n) {
      const patch = grid.patch(p, n);
      if (patch) ents.push(patch);
    }
  }
  for (const e of far) {
    const p = sent.get(e.id);
    if (!p) {
      const n = grid.net(e);
      ents.push({ ...n });
      next.set(e.id, n);
      continue;
    }
    if (!farDue) {
      next.set(e.id, p);
      continue;
    }
    const n = grid.net(e);
    let patch: NetEntityPatch | null = null;
    for (const k of FAR_FIELDS) if (p[k] !== n[k]) (patch ??= { id: e.id })[k] = n[k] as never;
    if (patch) {
      const full = { ...p, ...patch };
      ents.push(pack(patch, p, full));
      next.set(e.id, full);
    } else next.set(e.id, p);
  }
  const gone: number[] = [];
  for (const id of sent.keys()) if (!next.has(id)) gone.push(id);
  return { ents, gone, sent: next };
}

const sameCast = (a: NetEntity['cast'], b: NetEntity['cast']): boolean =>
  a === b ||
  (!!a &&
    !!b &&
    a.skillId === b.skillId &&
    a.kind === b.kind &&
    a.t === b.t &&
    a.windup === b.windup &&
    a.at?.x === b.at?.x &&
    a.at?.y === b.at?.y);

/** The fields of `n` that differ from `p` (null when none). */
function diffOne(p: NetEntity, n: NetEntity): NetEntityPatch | null {
  let patch: NetEntityPatch | null = null;
  for (const k of Object.keys(n) as (keyof NetEntity)[]) {
    if (k === 'id') continue;
    if (k === 'cast' ? sameCast(p.cast, n.cast) : k === 'kidnap' ? sameKidnap(p.kidnap, n.kidnap) : p[k] === n[k])
      continue;
    ((patch ??= { id: n.id }) as Record<string, unknown>)[k] = n[k];
  }
  return patch;
}

/** What to send: the entities new or changed since `prev` (changed fields only), and the ids gone. */
export function diffEntities(
  prev: ReadonlyMap<number, NetEntity>,
  next: readonly NetEntity[],
): { ents: NetEntityPatch[]; gone: number[] } {
  const ents: NetEntityPatch[] = [];
  const seen = new Set<number>();
  for (const n of next) {
    seen.add(n.id);
    const p = prev.get(n.id);
    if (!p) {
      ents.push({ ...n });
      continue;
    }
    const patch = diffOne(p, n);
    if (patch) ents.push(patch);
  }
  const gone = [...prev.keys()].filter((id) => !seen.has(id));
  return { ents, gone };
}

/**
 * The client's side of `diffEntities`: the new state, as a new map (entities that changed are new
 * objects; the rest are shared with `prev`). A packed move steps x and y. A patch for an
 * unknown id without a kind is ignored.
 */
export function patchEntities(
  prev: ReadonlyMap<number, NetEntity>,
  ents: readonly NetPatch[],
  gone: readonly number[],
): Map<number, NetEntity> {
  const out = new Map(prev);
  for (const id of gone) out.delete(id);
  for (const item of ents) {
    if (Array.isArray(item)) {
      const p = out.get(item[0]);
      if (p) out.set(p.id, { ...p, x: step(p.x, item[1]), y: step(p.y, item[2]) });
      continue;
    }
    const patch = item;
    const p = out.get(patch.id);
    if (p) out.set(patch.id, { ...p, ...patch });
    else if (patch.kind !== undefined) out.set(patch.id, patch as NetEntity);
  }
  return out;
}

/**
 * Between two states (`f` from 0 at `a` to 1 at `b`): positions, bars and timers blended, the rest
 * as in `b`. An entity only in `b`, or one that jumped further than a step, is shown as in `b`.
 */
export function blendEntities(
  a: ReadonlyMap<number, NetEntity>,
  b: ReadonlyMap<number, NetEntity>,
  f: number,
): Map<number, NetEntity> {
  const t = Math.min(1, Math.max(0, f));
  const out = new Map<number, NetEntity>();
  const mix = (x: number, y: number) => x + (y - x) * t;
  for (const [id, nb] of b) {
    const na = a.get(id);
    if (!na || t >= 1 || Math.hypot(nb.x - na.x, nb.y - na.y) > SNAP_DISTANCE) {
      out.set(id, nb);
      continue;
    }
    out.set(id, {
      ...nb,
      x: mix(na.x, nb.x),
      y: mix(na.y, nb.y),
      z: mix(na.z, nb.z),
      hp: mix(na.hp, nb.hp),
      qi: mix(na.qi, nb.qi),
      stunT: mix(na.stunT, nb.stunT),
      swingT: mix(na.swingT, nb.swingT),
      hitT: mix(na.hitT, nb.hitT),
      cast:
        nb.cast && na.cast && na.cast.skillId === nb.cast.skillId
          ? { ...nb.cast, t: mix(na.cast.t, nb.cast.t) }
          : nb.cast,
    });
  }
  return out;
}

/**
 * Writes server state onto a replica World. `you` (the server's id for this client's character)
 * lands on the replica's own `player`; any other entity lands on the replica's entity of the same
 * id and kind, and is skipped when there is none (the replica only holds what its zone spawned).
 * Returns how many entities were written.
 */
export function applyEntities(world: World, ents: Iterable<NetEntity>, you: number | null): number {
  let n = 0;
  for (const s of ents) {
    const e = s.id === you ? world.player : world.get(s.id);
    if (!e || (s.id !== you && (e === world.player || e.kind !== s.kind))) continue;
    e.x = s.x;
    e.y = s.y;
    e.z = s.z;
    e.facing = s.facing;
    e.heading = s.heading;
    e.dirX = s.dirX;
    e.dirY = s.dirY;
    e.moving = s.moving;
    e.sprinting = s.sprinting;
    e.dead = s.dead;
    e.blocking = s.blocking;
    e.sheathed = s.sheathed;
    e.hp = s.hp;
    e.maxHp = s.maxHp;
    e.qi = s.qi;
    e.maxQi = s.maxQi;
    e.stunT = s.stunT;
    e.swingT = s.swingT;
    e.hitT = s.hitT;
    e.cast = s.cast ? { ...s.cast, targetId: null } : null;
    e.mount = s.mount;
    e.emote = s.emote;
    e.act = s.act;
    e.actUntil = s.actUntil;
    e.targetId = s.targetId;
    // Figure, worn looks, name, title and size (older servers leave them out, or send players' only: the replica's stay).
    if (s.sprite !== undefined) e.sprite = s.sprite;
    if (s.outfit !== undefined && s.outfit !== e.outfit.join(',')) e.outfit = s.outfit ? s.outfit.split(',') : [];
    if (s.name !== undefined) e.name = s.name;
    if (s.title !== undefined) e.title = s.title;
    if (s.scale !== undefined) e.scale = s.scale;
    if (s.radius !== undefined) e.radius = s.radius;
    applyKidnap(e, s.kidnap);
    n++;
  }
  return n;
}

/** Whether the replica's own entity `e` is the one the server shows as `s` (a different one under the same id is replaced). */
function sameThing(e: Entity, s: NetEntity): boolean {
  if (e.kind !== s.kind) return false;
  if (s.mon !== undefined && e.brain?.defId !== s.mon) return false;
  // A player's figure changes with its outfit; anyone else's figure is who they are.
  return s.kind === 'player' || s.sprite === undefined || e.sprite === s.sprite;
}

/** A new entity on the replica for one the server shows (`replicaAdd`): enough to draw it, name it and target it. */
function standIn(s: NetEntity): Partial<Entity> & { id: number; kind: EntityKind; x: number; y: number } {
  const o: Partial<Entity> & { id: number; kind: EntityKind; x: number; y: number } = {
    id: s.id,
    kind: s.kind,
    x: s.x,
    y: s.y,
    z: s.z,
    facing: s.facing,
    hp: s.hp,
    maxHp: s.maxHp,
    qi: s.qi,
    maxQi: s.maxQi,
    ...(s.name !== undefined ? { name: s.name } : {}),
    ...(s.title !== undefined ? { title: s.title } : {}),
    ...(s.sprite !== undefined ? { sprite: s.sprite } : {}),
    ...(s.outfit ? { outfit: s.outfit.split(',') } : {}),
    ...(s.scale !== undefined ? { scale: s.scale } : {}),
    ...(s.radius !== undefined ? { radius: s.radius } : {}),
  };
  const def = s.mon !== undefined ? MONSTERS[s.mon] : undefined;
  if (s.mon !== undefined) {
    // The renderer and labels read a monster's kind from its brain (never run on a replica).
    o.brain = { defId: s.mon, spawnX: s.x, spawnY: s.y, state: 'idle', decisionT: 0, blockT: 0, foeId: null };
    if (def) {
      o.level = def.level;
      o.sprite ??= def.sprite;
      o.scale ??= def.scale;
      o.name ??= def.name;
    }
  }
  if (s.kind === 'rival') o.rival = { homeX: s.x, homeY: s.y, aggroT: 0, skillT: 0, skills: [] };
  return o;
}

/**
 * A replica's stand-ins for what it did not build: other players, and anything else the server shows
 * that the replica holds no match for (a bout's foe, a room's people, a monster spawned since). Each
 * is made under the server's id the first time it is shown (a different entity of the replica's under
 * that id is replaced) and removed when the server stops showing it; `standIns` keeps the ids (the
 * caller's, one per replica). The replica's own player and pet are never replaced. Run before
 * `applyEntities`, which then moves them like the rest.
 */
export function syncStandIns(
  world: World,
  ents: Iterable<NetEntity>,
  you: number | null,
  standIns: Set<number>,
): void {
  const seen = new Set<number>();
  for (const s of ents) {
    if (s.id === you) continue;
    const have = world.entities.get(s.id);
    if (have === world.player || have === world.pet) continue;
    const mine = standIns.has(s.id);
    // A stand-in's figure is written by `applyEntities`; only a new kind (or monster) under its id remakes it.
    const keep = have && (mine ? have.kind === s.kind && (s.mon === undefined || have.brain?.defId === s.mon) : sameThing(have, s));
    if (keep) {
      if (mine) seen.add(s.id);
      continue;
    }
    if (have) world.replicaRemove(s.id);
    standIns.delete(s.id);
    if (world.replicaAdd(standIn(s))) {
      standIns.add(s.id);
      seen.add(s.id);
    }
  }
  for (const id of standIns)
    if (!seen.has(id)) {
      world.replicaRemove(id);
      standIns.delete(id);
    }
}

/**
 * A replica's own clock between snapshots: the World's time and every entity's animation phase,
 * and the hour of the day and the weather as the server last gave them.
 */
export function advanceReplica(world: World, dt: number, hour?: number, weather?: WeatherState): void {
  world.time += dt;
  if (hour !== undefined) world.hour = hour;
  if (weather) world.weather = weather;
  for (const e of world.entities.values()) e.animT += dt;
}
